import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  StarIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { translateApiError } from '../../utils/apiError';
import AuthPageShell from '../ui/AuthPageShell';
import InputIcon from '../ui/InputIcon';
import Label from '../ui/Label';
import { CenteredLoaderSkeleton } from '../ui/PageSkeleton';
import Button from '../ui/Button';
import BrandMark from '../ui/BrandMark';
import { validatePassword, validatePasswordMatch, validateFields } from '../../utils/formValidation';
import { inputClassWithError } from '../../styles/forms';

function ResetPassword() {
  const { t, formatMessage } = useLanguage();
  const rp = t.resetPassword;
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [validToken, setValidToken] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const validation = t.ux?.validation || {};
  const { token } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function validateToken() {
      if (!token) {
        setValidToken(false);
        return;
      }

      try {
        const response = await fetch(`/api/reset-password/${token}`);
        const data = await response.json();
        if (!cancelled) {
          setValidToken(Boolean(data?.valid));
        }
      } catch {
        if (!cancelled) {
          setValidToken(false);
        }
      }
    }

    validateToken();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    setPasswordStrength(strength);
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { valid, errors } = validateFields({
      password: validatePassword(password, {
        required: validation.passwordRequired || rp.passwordTooShort,
        min: validation.passwordMin || rp.passwordTooShort,
      }, 6),
      confirmPassword: validatePasswordMatch(
        password,
        confirmPassword,
        validation.passwordMismatch || rp.passwordMismatch
      ),
    });
    setFieldErrors(errors);
    if (!valid) return;

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`/api/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(rp.passwordUpdated);
        toast.success(rp.passwordUpdated);
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setValidToken(false);
        const errMsg = translateApiError(data, t, formatMessage, rp.resetError);
        setMessage(errMsg);
        toast.error(errMsg);
      }
    } catch {
      setMessage(rp.connectionError);
      toast.error(rp.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: ShieldCheckIcon, title: rp.features.security.title, description: rp.features.security.desc },
    { icon: StarIcon, title: rp.features.instant.title, description: rp.features.instant.desc },
    { icon: RocketLaunchIcon, title: rp.features.continuity.title, description: rp.features.continuity.desc },
  ];

  const strengthColor =
    passwordStrength <= 2 ? 'bg-error-400' : passwordStrength <= 3 ? 'bg-warning-400' : 'bg-success-400';

  const strengthText =
    passwordStrength === 0
      ? ''
      : passwordStrength <= 2
        ? rp.weak
        : passwordStrength <= 3
          ? rp.medium
          : rp.strong;

  if (validToken === null) {
    return (
      <AuthPageShell backTo="/forgot-password" backLabel={rp.requestNewLink}>
        <CenteredLoaderSkeleton />
      </AuthPageShell>
    );
  }

  if (!validToken) {
    return (
      <AuthPageShell backTo="/forgot-password" backLabel={rp.requestNewLink}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="auth-card mx-auto max-w-md w-full p-8 text-center"
        >
          <div className="icon-tile mx-auto mb-4 h-16 w-16 from-error-500 to-error-600">
            <LockClosedIcon className="h-8 w-8 text-white" />
          </div>
          <h2 className="mb-2 text-2xl font-bold text-secondary-900">{rp.invalidLink}</h2>
          <p className="mb-6 text-secondary-600">{rp.invalidLinkDesc}</p>
          <Button
            to="/forgot-password"
            variant="primary"
            icon={ArrowRightIcon}
            iconPosition="right"
            className="w-full py-3"
          >
            {rp.requestNewLink}
          </Button>
        </motion.div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell backTo="/login" backLabel={rp.backToLogin}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-6 px-3 safe-x sm:px-4 lg:grid-cols-2 lg:gap-12"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05 }}
          className="hidden text-center lg:block lg:text-left"
        >
          <BrandMark variant="inline" className="mb-6 hidden lg:block" />
          <h2 className="text-3xl font-bold text-secondary-900">
            {rp.new}
            <span className="text-gradient mt-1 block">{rp.titleHighlight}</span>
          </h2>
          <p className="mt-3 max-w-md text-secondary-600">{rp.subtitle}</p>
          <div className="mt-8 space-y-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + index * 0.08 }}
                className="feature-row"
              >
                <div className="icon-tile h-12 w-12 from-orange-500 to-amber-500">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-secondary-900">{feature.title}</h3>
                  <p className="text-sm text-secondary-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="mx-auto w-full max-w-md"
        >
          <div className="auth-card p-6 sm:p-8 lg:p-10">
            <div className="mb-8 text-center">
              <h3 className="text-2xl font-bold text-secondary-900">{rp.createNewPassword}</h3>
              <p className="mt-2 text-secondary-600">{rp.chooseSecure}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="reset-password">{rp.newPassword}</Label>
                <InputIcon icon={LockClosedIcon}>
                  <input
                    id="reset-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (fieldErrors.password) setFieldErrors((prev) => ({ ...prev, password: null }));
                    }}
                    className={inputClassWithError(Boolean(fieldErrors.password), 'input-premium pr-12')}
                    placeholder={rp.enterNewPassword}
                    autoComplete="new-password"
                    aria-invalid={fieldErrors.password ? true : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-lg p-1 text-secondary-400 transition-colors hover:text-secondary-600"
                    aria-label={showPassword ? t.common.hidePassword : t.common.showPassword}
                  >
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </InputIcon>
                {password ? (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2.5">
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-secondary-500">{rp.passwordStrength}</span>
                      <span className="font-medium text-secondary-700">{strengthText}</span>
                    </div>
                    <div className="strength-meter">
                      <div
                        className={`strength-meter-fill ${strengthColor}`}
                        style={{ width: `${(passwordStrength / 5) * 100}%` }}
                      />
                    </div>
                  </motion.div>
                ) : null}
                {fieldErrors.password ? (
                  <p role="alert" className="mt-1.5 text-xs font-medium text-error-600">{fieldErrors.password}</p>
                ) : null}
              </div>

              <div>
                <Label htmlFor="reset-confirm-password">{rp.confirmPassword}</Label>
                <InputIcon icon={LockClosedIcon}>
                  <input
                    id="reset-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (fieldErrors.confirmPassword) {
                        setFieldErrors((prev) => ({ ...prev, confirmPassword: null }));
                      }
                    }}
                    className={inputClassWithError(Boolean(fieldErrors.confirmPassword), 'input-premium pr-12')}
                    placeholder={rp.confirmNewPassword}
                    autoComplete="new-password"
                    aria-invalid={fieldErrors.confirmPassword ? true : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-lg p-1 text-secondary-400 transition-colors hover:text-secondary-600"
                    aria-label={showConfirmPassword ? t.common.hidePassword : t.common.showPassword}
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </InputIcon>
                {fieldErrors.confirmPassword ? (
                  <p role="alert" className="mt-1.5 text-xs font-medium text-error-600">{fieldErrors.confirmPassword}</p>
                ) : null}
              </div>

              {password && confirmPassword ? (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`flex items-center gap-2 text-sm ${
                    password === confirmPassword ? 'text-success-600' : 'text-error-600'
                  }`}
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  {password === confirmPassword ? rp.passwordsMatch : rp.passwordsNoMatch}
                </motion.p>
              ) : null}

              {message ? (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={
                    message.toLowerCase().includes('error') ? 'alert-soft-error' : 'alert-soft-success'
                  }
                >
                  {message}
                </motion.div>
              ) : null}

              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading || password !== confirmPassword || password.length < 6}
                icon={ArrowRightIcon}
                iconPosition="right"
                className="w-full py-3.5"
              >
                {rp.updatePassword}
              </Button>

              <div className="border-t border-secondary-200/50 pt-4 text-center">
                <Link to="/login" className="text-sm font-medium text-orange-600 transition-colors hover:text-orange-700">
                  {rp.backToLogin}
                </Link>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AuthPageShell>
  );
}

export default ResetPassword;
